import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ApplicationModel from '../../../../../../shared/models/applications/api/ApplicationModel';
import { dark_grey_blue, color_shades_darker, color_colors_ocean } from '../../../../../../shared/constants/colors';
import { ModuleFieldLocation, findModuleField, updateModuleFieldAndFinalize } from '../../../../../../shared/utils/moduleFieldFinalize';
import { TextLinkButton } from '../../../../../../shared/scenes/organizationSettings/components/MedicalIntegrationConfigSettings';
import { ToolbarSelect } from './PmsLookupPanels';
import { PillError, SparkFormButton } from './sparkFormPills';

// A change picked from a lookup row, waiting for Save & Sync so one click can't change the form
export interface PendingFormChange {
    value: string;
    // Which row's button the change came from
    key: string;
    // What the change does, e.g. "Make Dr. Smith a preferred provider"
    description: string;
}

interface FieldTarget {
    app: ApplicationModel;
    field: ModuleFieldLocation;
    isShared: boolean;
}

export const ALL_LOCATIONS = '';

export const sameFormValue = (a?: string | null, b?: string | null) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

// "null" and "-1" are how an unset optional field is saved (resolveEffectiveDentalFormFieldValues treats both as unset)
export const isUnsetFormValue = (value?: string | null) => !value || ['null', '-1'].includes(value.trim().toLowerCase());

const sameTarget = (a: FieldTarget | null, b: FieldTarget) =>
    !!a && a.app.id === b.app.id && a.field.appliedTemplate.id === b.field.appliedTemplate.id
    && a.field.templateFormFieldId === b.field.templateFormFieldId && a.field.dynamicFormFieldName === b.field.dynamicFormFieldName;

// One Spark form field for a picked location, falling back to the Shared Configuration's,
// with a save that finishes the form and queues a sync like the module editor does
export const useSparkFormField = (locationApps: ApplicationModel[], officeId: string, variable: string, onSaved?: (appId: string, isShared: boolean) => void) => {
    const locations = useMemo(() => locationApps
        .filter(app => !app.allowsTemplating)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')), [locationApps]);
    const sharedApp = locationApps.find(app => app.allowsTemplating);
    const matchingLocation = locations.find(app => app.pmsLocationId && app.pmsLocationId === officeId);

    const [locationId, setLocationId] = useState<string>(matchingLocation?.id ?? ALL_LOCATIONS);
    const [target, setTarget] = useState<FieldTarget | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [savingValue, setSavingValue] = useState<string | null>(null);
    const [pending, setPending] = useState<PendingFormChange | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Only the latest location read may set the target, so a slow read for a previous location can't win
    const loadGeneration = useRef(0);

    // Follow the office picker to its location when the location's PMS ID matches
    useEffect(() => {
        if (matchingLocation) setLocationId(matchingLocation.id);
    }, [officeId]);

    useEffect(() => {
        const generation = ++loadGeneration.current;
        const isCurrent = () => generation === loadGeneration.current;
        const load = async () => {
            // Clear the previous location's target first so nothing can be saved to it while this one loads
            setTarget(null);
            setPending(null);
            setIsLoading(true);
            setError(null);
            const location = locations.find(app => app.id === locationId);
            let resolved: FieldTarget | null = null;
            if (location) {
                const result = await findModuleField(location.id, variable);
                if (!isCurrent()) return;
                if (result.resultType !== 'Ok') {
                    // The location may have its own value; falling back would change the Shared Configuration instead
                    setError(`Couldn't read ${location.name}'s modules: ${result.errors?.join(', ') || 'Unknown error'}`);
                    setIsLoading(false);
                    return;
                }
                if (result.data) {
                    resolved = { app: location, field: result.data, isShared: false };
                }
            }
            if (!resolved && sharedApp) {
                const result = await findModuleField(sharedApp.id, variable);
                if (!isCurrent()) return;
                if (result.resultType === 'Ok' && result.data) {
                    resolved = { app: sharedApp, field: result.data, isShared: true };
                } else if (result.resultType !== 'Ok') {
                    setError(`Couldn't read the Shared Configuration's modules: ${result.errors?.join(', ') || 'Unknown error'}`);
                }
            }
            if (!isCurrent()) return;
            setTarget(resolved);
            setIsLoading(false);
        };
        load();
    }, [locationId, variable]);

    // Rows only propose a change; nothing is saved until confirm
    const request = (value: string, description: string, key: string = value) => {
        if (!target || isLoading || savingValue) return;
        setError(null);
        setPending({ value, key, description });
    };

    const cancel = () => {
        if (!savingValue) setPending(null);
    };

    // Saves the pending value; its key marks which row's button shows "Updating..."
    const confirm = async () => {
        if (!target || isLoading || !pending) return;
        const savedTarget = target;
        const { value, key } = pending;
        setSavingValue(key);
        setError(null);
        const result = await updateModuleFieldAndFinalize(savedTarget.app.id, savedTarget.field, value);
        setSavingValue(null);
        setPending(null);
        if (result.resultType === 'Ok') {
            // Only update the target this save was for; the location may have changed meanwhile
            setTarget(current => current && sameTarget(current, savedTarget) ? { ...current, field: { ...current.field, value } } : current);
            onSaved?.(savedTarget.app.id, savedTarget.isShared);
        } else {
            setError(`Couldn't update: ${result.errors?.join(', ') || 'Unknown error'}`);
        }
    };

    const rawValue = target?.field.value?.trim() || null;
    const current = isUnsetFormValue(rawValue) ? null : rawValue;

    return { locations, locationId, setLocationId, target, current, isLoading, request, confirm, cancel, pending, savingValue, error };
};

type SparkFormField = ReturnType<typeof useSparkFormField>;

interface SparkFormBarProps {
    form: SparkFormField;
    // The current value's pill, shown once the field has loaded
    status: React.ReactNode;
}

// Location picker plus the field's current value, for the top of a lookup
export const SparkFormBar: React.FC<SparkFormBarProps> = ({ form, status }) => (
    <FormBar>
        <BarLabel>Spark form</BarLabel>
        <LocationSelect aria-label="Location" value={form.locationId} disabled={!!form.savingValue || !!form.pending} onChange={e => form.setLocationId(e.target.value)}>
            <option value={ALL_LOCATIONS}>All locations</option>
            {form.locations.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
        </LocationSelect>
        {form.isLoading ? <Muted>Checking...</Muted> : !form.target ? <Muted>No Spark form field found</Muted> : status}
        {form.target && form.locationId !== ALL_LOCATIONS && form.target.isShared && !form.isLoading && <Muted>from Shared Configuration</Muted>}
        {form.error && <PillError title={form.error}>{form.error}</PillError>}
    </FormBar>
);

// Save & Sync for the change a row proposed, shown in that row's Spark form cell in place of its button
export const SparkFormRowConfirm: React.FC<{ form: SparkFormField; rowKey: string }> = ({ form, rowKey }) => {
    if (form.pending?.key !== rowKey || !form.target) return null;
    const isSaving = !!form.savingValue;
    const destination = form.target.isShared ? 'the Shared Configuration' : form.target.app.name;
    return (
        <RowConfirm>
            <RowConfirmActions>
                <SaveSyncButton type="button" onClick={form.confirm} disabled={isSaving} title={`${form.pending.description}: save to ${destination} and queue a sync. This won't publish.`}>
                    {isSaving ? 'Saving...' : 'Save & Sync'}
                </SaveSyncButton>
                <TextLinkButton type="button" onClick={form.cancel} disabled={isSaving}>Cancel</TextLinkButton>
            </RowConfirmActions>
            <Muted>To {destination} · won't publish</Muted>
        </RowConfirm>
    );
};

const RowConfirm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const RowConfirmActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SaveSyncButton = styled(SparkFormButton)`
    background-color: ${color_colors_ocean};
    color: #ffffff;
`;

const FormBar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const BarLabel = styled.span`
    font-family: Muli;
    font-size: 13px;
    font-weight: 700;
    color: ${dark_grey_blue};
`;

const LocationSelect = styled(ToolbarSelect)`
    padding: 5px 32px 5px 12px;
    min-width: 140px;
`;

export const Muted = styled.span`
    font-family: Muli;
    font-size: 12px;
    color: ${color_shades_darker};
`;
